// src/pages/Analytics.jsx
// Analytics (3.49.0, Super Admins only; backend lib/analytics.js): who
// visits the Fax Portal, like Google Analytics but our own (nothing goes to
// Google). Two views (owner, 2026-10-09):
//   Clients & visitors  everyone except Super Admins, the sign-in page too
//   Super Admins        only Super Admins' own visits
// For today, 7, 30 or 90 days or 12 months: visitors (new and returning),
// visits, page views, signed-in people, sign-in page → signed in, who's here
// now, visits by day, busiest hours (Eastern Time), top pages, countries and
// cities, browsers, systems and devices, referring sites, and the latest
// visits. Refreshes when asked (each load reads one record per day).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import { TABS } from '../utils/tabRoutes';
import { PAGE_TITLE, SECTION_SUB, PILL, PILL_ON, PILL_ROW, TH_SELECT, TH_OPTION } from '../theme/tabs';
import { useSort } from '../components/SortTh';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

const RANGES = [['today', 'Today'], ['7', '7 days'], ['30', '30 days'], ['90', '90 days'], ['365', '12 months']];
const SCOPES = [['main', '👥 Clients & visitors'], ['super', '🛡️ Super Admins']];
const num = n => Number(n || 0).toLocaleString('en-US');
const fmt = iso => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const OTHER = { signin: 'Sign-in page', 'set-password': 'Set your password', 'reset-password': 'Reset password', 'request-access': 'Ask for an account', control: 'Console' };
function pageName(p) {
  const [a, b] = String(p || '').split('/');
  const first = OTHER[a] || TABS.find(t => t.id === a)?.label || a;
  return b ? `${first} → ${b.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase())}` : first;
}

// Provider names can be long ("Atria Convergence Technologies Pvt. Ltd.
// Broadband Internet Service Provider IND"): the description after the
// company is dropped and anything still long ends in "…"; the full name
// shows on hover, and searches still match it
const shortProvider = p => String(p || '').replace(/\s+(Broadband|Internet Service|Internet Services|ISP\b|Telecom Service).*$/i, '').trim() || String(p || '');
const clip = (t, n = 40) => (t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t);
const providerLabel = p => clip(shortProvider(p));
function Provider({ name }) {
  return name ? <span title={name}>{providerLabel(name)}</span> : '—';
}

export default function Analytics() {
  const { getToken } = useAuth();
  const [range, setRange] = useState('30');
  const [scope, setScope] = useState('main');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Filters for the Visitors list and the latest visits: country, new or
  // returning, signed in or not, and a search (address, email, city, provider)
  const [filter, setFilter] = useState({ country: '', kind: 'all', who: 'all', q: '' });
  const visitorsRef = useRef(null);
  const pickCountry = c => { setFilter(f => ({ ...f, country: c === 'Unknown' ? '' : c })); visitorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/portal/control/analytics?range=${range}&scope=${scope}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || "Couldn't load the figures.");
      setData(j); setError('');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [range, scope, getToken]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  return (
    <div>
      <h2 style={PAGE_TITLE} className="cp-title"><Emo e="📊" /> Analytics</h2>
      <p style={{ ...SECTION_SUB, marginTop: -6, marginBottom: 14 }}>
        Who visits the Fax Portal: visitors, visits and pages, where they come from and what they use. Counted by the portal itself, so nothing goes to Google.
        {scope === 'main' ? ' Super Admins are left out of these figures; see them under Super Admins.' : ' Only Super Admins’ own visits.'}
      </p>
      <div style={S.bar}>
        <div style={PILL_ROW} role="tablist" aria-label="Whose visits">
          {SCOPES.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={scope === id} onClick={() => setScope(id)} style={{ ...PILL, ...(scope === id ? PILL_ON : {}) }}>
              <Emo e={label.split(' ')[0]} /> {label.split(' ').slice(1).join(' ')}
            </button>
          ))}
        </div>
        <div style={PILL_ROW} role="tablist" aria-label="Period">
          {RANGES.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={range === id} onClick={() => setRange(id)} style={{ ...PILL, ...(range === id ? PILL_ON : {}) }}>{label}</button>
          ))}
        </div>
        <button onClick={load} disabled={loading} style={S.refresh}>{loading ? 'Loading…' : <><Emo e="↻" /> Refresh</>}</button>
      </div>
      {error && <div style={S.error}>{error}</div>}
      {!data ? <Loader label="Counting visits…" /> : (
        <>
          <div style={S.cards}>
            {/* 3.67.2 (owner): signed-in people as Online now counts them (open and active in the last 5 minutes), plus visitors not signed in */}
            <Card icon="🟢" label="On the portal now" value={num(data.now.visitors)} live
              sub={!data.now.visitors ? 'nobody in the last 5 minutes'
                : [data.now.signedIn != null ? `${num(data.now.signedIn)} signed in${data.now.guests ? ` · ${num(data.now.guests)} not signed in` : ''}` : '', data.now.pages.map(pageName).slice(0, 3).join(', ')].filter(Boolean).join(' · ')} />
            <Card icon="👤" label="Visitors" value={num(t.visitors)} sub={`${num(t.newVisitors)} new · ${num(t.returningVisitors)} returning`} />
            <Card icon="🔁" label="Visits" value={num(t.visits)} sub={`${t.viewsPerVisit} pages a visit`} />
            <Card icon="📄" label="Page views" value={num(t.views)} />
            <Card icon="🔑" label="Signed-in people" value={num(t.people)} sub={`${num(t.signins)} sign-ins`} />
            <Card icon="🚪" label="Sign-in page views" value={num(t.signinViews)} sub={t.signinViews ? `${Math.min(100, Math.round((t.signins / t.signinViews) * 100))}% went on to sign in` : ''} />
          </div>
          {data.range !== 'today' && (
            <Panel title="📈 Visitors by day" note={`${data.from} to ${data.to} (Eastern Time)`}>
              <div style={S.legend}><span><i style={{ ...S.key, background: 'var(--c-00897b)' }} />Visitors</span><span><i style={{ ...S.key, height: 3, background: 'var(--c-1e88e5)', verticalAlign: '3px' }} />Page views</span></div>
              <Daily rows={data.daily} />
            </Panel>
          )}
          <Panel title="🕒 Busiest hours" note="Page views by hour, Eastern Time"><Hours hours={data.hours} /></Panel>
          <div style={S.grid}>
            <Panel title="📄 Top pages"><List rows={data.pages.map(r => ({ ...r, label: pageName(r.label) }))} /></Panel>
            <Panel title="🌍 Countries" note="Click one to see its visitors"><List rows={data.countries} onPick={pickCountry} /></Panel>
            <Panel title="🏙️ Cities"><List rows={data.cities.map(r => ({ ...r, label: r.label.replace(/^\(unknown\), /, 'Somewhere in ') }))} /></Panel>
            <Panel title="🖥️ Devices"><List rows={data.devices} /></Panel>
            <Panel title="🌐 Browsers"><List rows={data.browsers} /></Panel>
            <Panel title="💻 Systems"><List rows={data.systems} /></Panel>
            <Panel title="🏢 Providers" note="The network each visitor used (internet company, mobile carrier…)"><List rows={(data.providers || []).map(r => ({ ...r, full: r.label, label: providerLabel(r.label) }))} /></Panel>
            <Panel title="🔗 Came from" note="Other websites that linked here"><List rows={data.refs} empty="Nobody came from another website." /></Panel>
          </div>
          <div ref={visitorsRef} style={{ scrollMarginTop: 180 }}>
            <Visitors data={data} filter={filter} setFilter={setFilter} />
          </div>
          <LatestVisits rows={data.recent} />
          <p style={S.foot}>Locations and providers come from the visitor's IP address and are approximate. This product includes GeoLite2 data created by MaxMind, available from maxmind.com; providers from iptoasn.com (public domain).</p>
        </>
      )}
    </div>
  );
}

// ── The Visitors list's filters ──
const filterActive = f => !!(f.country || f.q.trim() || f.kind !== 'all' || f.who !== 'all');
const hasText = (q, ...parts) => !q || parts.join(' ').toLowerCase().includes(q);
function visitorMatches(v, f) {
  const q = f.q.trim().toLowerCase();
  return (!f.country || v.country === f.country)
    && (f.who === 'all' || (f.who === 'signed' ? v.emails.length > 0 : v.emails.length === 0))
    && (f.kind === 'all' || (f.kind === 'new' ? !v.returning : v.returning))
    && hasText(q, v.ips.join(' '), v.emails.join(' '), v.city, v.region, v.country, v.provider);
}

// One row per visitor (per browser) seen in the period: how often they came
// back, who they are when signed in, where from and on what
// 3.49.20 (owner): long lists show 50 at a time, "Show next 50" at the bottom right
const STEP = 50;
function MoreRows({ total, shown, onMore, onFirst }) {
  if (total <= STEP) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 10, fontSize: 12.5, color: 'var(--t-78909c)' }}>
      <span>Showing {num(Math.min(shown, total))} of {num(total)}</span>
      {shown > STEP && <button onClick={onFirst} style={S.clear}>Show the first 50</button>}
      {shown < total && <button onClick={onMore} style={{ ...S.clear, fontWeight: 700 }}>Show next {num(Math.min(STEP, total - shown))}</button>}
    </div>
  );
}

function Visitors({ data, filter, setFilter }) {
  const [shownN, setShownN] = useState(STEP);   // 3.49.20: 50 at a time
  const countries = useMemo(() => [...new Set(data.visitors.map(v => v.country).filter(Boolean))].sort(), [data.visitors]);
  const filtered = useMemo(() => data.visitors.filter(v => visitorMatches(v, filter)), [data.visitors, filter]);
  const { sorted: rows, sort, onSort } = useSort(filtered, {
    who: v => v.emails[0] || `~${v.id}`, visits: v => v.visits, views: v => v.views, first: v => v.firstDay, last: v => v.lastAt,
    where: v => [v.country, v.city].join(' '), provider: v => shortProvider(v.provider), ip: v => v.lastIp || v.ips[0], using: v => v.device,
  });
  const shown = rows.slice(0, shownN);
  const returning = rows.filter(v => v.returning).length;
  const set = (k, v) => setFilter(f => ({ ...f, [k]: v }));
  const day = d => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
  return (
    <Panel title="👥 Visitors" note="One row per visitor (per browser) seen in this period">
      <div style={S.filters}>
        <input value={filter.q} onChange={e => set('q', e.target.value)} placeholder="Find an IP address, email, city or provider" aria-label="Find a visitor" style={{ ...S.fInput, flex: '1 1 240px' }} />
        <select value={filter.country} onChange={e => set('country', e.target.value)} aria-label="Country" style={S.fInput}>
          <option value="">All countries</option>
          {(filter.country && !countries.includes(filter.country) ? [filter.country, ...countries] : countries).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filter.kind} onChange={e => set('kind', e.target.value)} aria-label="New or returning" style={S.fInput}>
          <option value="all">New and returning</option><option value="new">New only</option><option value="returning">Returning only</option>
        </select>
        <select value={filter.who} onChange={e => set('who', e.target.value)} aria-label="Signed in or not" style={S.fInput}>
          <option value="all">Signed in or not</option><option value="signed">Signed in</option><option value="anon">Not signed in</option>
        </select>
        {filterActive(filter) && <button onClick={() => setFilter({ country: '', kind: 'all', who: 'all', q: '' })} style={S.clear}>Clear filters</button>}
      </div>
      <div style={S.count}>{num(rows.length)} visitor{rows.length === 1 ? '' : 's'}{rows.length ? ` · ${num(returning)} returning · ${num(rows.length - returning)} new` : ''}{data.visitors.length >= 1000 ? ' (the 1,000 most recent)' : ''}</div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            {[['who', 'Visitor'], ['visits', 'Visits'], ['views', 'Pages'], ['first', 'First seen'], ['last', 'Last seen'], ['where', 'Where'], ['provider', 'Provider'], ['ip', 'IP addresses'], ['using', 'Using']]
              .map(([k, label]) => <Th key={k} k={k} label={label} sort={sort} onSort={onSort} />)}
          </tr></thead>
          <tbody>
            {shown.length === 0 ? <tr><td colSpan={9} style={S.empty}>{data.visitors.length ? 'No visitors match the filters.' : 'No visitors in this period yet.'}</td></tr> : shown.map((v, i) => (
              <tr key={v.id + i} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                <td style={S.td}>{v.emails.length ? v.emails.join(', ') : <span style={S.muted} title="This browser opened the portal but no one signed in on it">Not signed in<span style={{ display: 'block', fontSize: 11 }}>browser {v.id}</span></span>}
                  <div><span style={{ ...S.tag, ...(v.returning ? S.tagBack : S.tagNew) }}>{v.returning ? `Returning · ${v.daysSeen} day${v.daysSeen === 1 ? '' : 's'}` : 'New'}</span></div></td>
                <td style={{ ...S.td, textAlign: 'right' }}>{num(v.visits)}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>{num(v.views)}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{day(v.firstDay)}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmt(v.lastAt)}<div style={S.muted}>{pageName(v.lastPage)}</div></td>
                <td style={S.td}>{[v.city, v.region, v.country].filter(Boolean).join(', ') || '—'}</td>
                <td style={S.td}><Provider name={v.provider} /></td>
                <td style={S.td}>{v.ips.map(ip => <div key={ip}><code style={S.code}>{ip}</code></div>)}</td>
                <td style={S.td}>{v.browser} · {v.os} · {v.device}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MoreRows total={rows.length} shown={shownN} onMore={() => setShownN(n => n + STEP)} onFirst={() => setShownN(STEP)} />
    </Panel>
  );
}

// A table header: click the name to sort (again to reverse); an optional
// picker or search under it filters the column (as Contacts' Client column)
function Th({ k, label, sort, onSort, children = null }) {
  const on = sort?.key === k;
  return (
    <th style={S.th} aria-sort={on ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(k)} title={`Sort by ${label}`} style={S.sortBtn}>
        {label} <span aria-hidden="true" style={{ opacity: on ? 1 : 0.4, fontSize: 10 }}>{on ? (sort.dir > 0 ? '▲' : '▼') : '↕'}</span>
      </button>
      {children && <div style={{ marginTop: 5 }}>{children}</div>}
    </th>
  );
}
function Pick({ value, onChange, all, options, label }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} style={{ ...TH_SELECT, maxWidth: 170 }}>
      <option value="" style={TH_OPTION}>{all}</option>
      {options.map(([v, l]) => <option key={v} value={v} style={TH_OPTION} title={v}>{l}</option>)}
    </select>
  );
}

// The latest visits (newest first), with sort and filters in the headers
function LatestVisits({ rows }) {
  const blank = { page: '', who: '', country: '', provider: '', device: '', ip: '' };
  const [f, setF] = useState(blank);
  const set = k => v => setF(x => ({ ...x, [k]: v }));
  const uniq = get => [...new Set(rows.map(get).filter(Boolean))].sort();
  const filtered = useMemo(() => rows.filter(v => (!f.page || v.page === f.page)
    && (!f.who || (f.who === 'signed' ? !!v.email : f.who === 'anon' ? !v.email : f.who === 'new' ? !!v.isNew : v.email === f.who))
    && (!f.country || v.country === f.country) && (!f.provider || v.provider === f.provider) && (!f.device || v.device === f.device)
    && (!f.ip.trim() || String(v.ip).includes(f.ip.trim()))), [rows, f]);
  const [shownN, setShownN] = useState(STEP);   // 3.49.20: 50 at a time
  const { sorted, sort, onSort } = useSort(filtered, {
    at: v => v.at, page: v => pageName(v.page), who: v => v.email || '~', where: v => v.place, provider: v => shortProvider(v.provider), using: v => v.device, ip: v => v.ip,
  });
  const active = Object.values(f).some(Boolean);
  return (
    <Panel title="🧾 Latest visits" note={active ? `${num(sorted.length)} of ${num(rows.length)} shown` : 'Newest first (kept 90 days) · sort and filter in the column headers'}>
      {active && <button onClick={() => setF(blank)} style={{ ...S.clear, marginBottom: 10 }}>Clear column filters</button>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <Th k="at" label="When" sort={sort} onSort={onSort} />
            <Th k="page" label="Page" sort={sort} onSort={onSort}><Pick value={f.page} onChange={set('page')} all="All pages" label="Page" options={uniq(v => v.page).map(p => [p, pageName(p)])} /></Th>
            <Th k="who" label="Who" sort={sort} onSort={onSort}>
              <Pick value={f.who} onChange={set('who')} all="Everyone" label="Who" options={[['signed', 'Signed in'], ['anon', 'Not signed in'], ['new', 'New visitors'], ...uniq(v => v.email).map(e => [e, e])]} />
            </Th>
            <Th k="where" label="Where" sort={sort} onSort={onSort}><Pick value={f.country} onChange={set('country')} all="All countries" label="Country" options={uniq(v => v.country).map(c => [c, c])} /></Th>
            <Th k="provider" label="Provider" sort={sort} onSort={onSort}><Pick value={f.provider} onChange={set('provider')} all="All providers" label="Provider" options={uniq(v => v.provider).map(p => [p, providerLabel(p)])} /></Th>
            <Th k="using" label="Using" sort={sort} onSort={onSort}><Pick value={f.device} onChange={set('device')} all="All devices" label="Device" options={uniq(v => v.device).map(d => [d, d])} /></Th>
            <Th k="ip" label="Address" sort={sort} onSort={onSort}>
              <input value={f.ip} onChange={e => set('ip')(e.target.value)} placeholder="Find an IP" aria-label="Find an IP address" style={{ ...TH_SELECT, width: 120, cursor: 'text' }} />
            </Th>
          </tr></thead>
          <tbody>
            {sorted.length === 0 ? <tr><td colSpan={7} style={S.empty}>{rows.length ? 'No visits match these column filters.' : 'No visits yet.'}</td></tr> : sorted.slice(0, shownN).map((v, i) => (
              <tr key={i} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmt(v.at)}</td>
                <td style={S.td}>{pageName(v.page)}</td>
                <td style={S.td}>{v.email || <span style={S.muted}>Not signed in{v.isNew ? ' · new' : ''}</span>}</td>
                <td style={S.td}>{v.place || '—'}</td>
                <td style={S.td}><Provider name={v.provider} /></td>
                <td style={S.td}>{v.browser} · {v.os} · {v.device}</td>
                <td style={S.td}><code style={S.code}>{v.ip}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MoreRows total={sorted.length} shown={shownN} onMore={() => setShownN(n => n + STEP)} onFirst={() => setShownN(STEP)} />
    </Panel>
  );
}

function Card({ icon, label, value, sub, live }) {
  return (
    <div style={S.card} className="ov-lift">
      <div style={S.cardLabel}><Emo e={icon} /> {label}{live && <span style={S.liveDot} aria-hidden="true" />}</div>
      <div style={S.cardValue}>{value}</div>
      {sub && <div style={S.cardSub}>{sub}</div>}
    </div>
  );
}
function Panel({ title, note, children }) {
  return (
    <section style={S.panel}>
      <h3 style={S.h3}><Emo e={title.split(' ')[0]} /> {title.split(' ').slice(1).join(' ')}{note && <span style={S.note}>{note}</span>}</h3>
      {children}
    </section>
  );
}
function List({ rows, empty = 'Nothing yet.', onPick = null }) {
  if (!rows.length) return <div style={S.muted}>{empty}</div>;
  const max = Math.max(...rows.map(r => r.count));
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {rows.map(r => (
        <div key={r.label} style={{ ...S.listRow, ...(onPick ? { cursor: 'pointer' } : {}) }} {...(onPick ? { role: 'button', tabIndex: 0, title: `See visitors from ${r.label}`, onClick: () => onPick(r.label), onKeyDown: e => { if (e.key === 'Enter') onPick(r.label); } } : {})}>
          <div style={S.listBar}><span style={{ ...S.listFill, width: `${(r.count / max) * 100}%` }} /><span style={S.listLabel} title={r.full || r.label}>{r.label}</span></div>
          <strong style={S.listNum}>{num(r.count)}</strong>
        </div>
      ))}
    </div>
  );
}
function Daily({ rows }) {
  // Visitors as bars and page views as a line, each on its own scale (views
  // run about ten times higher), so both shapes show; the box gives numbers
  const [hover, setHover] = useState(null);
  const maxV = Math.max(1, ...rows.map(r => r.visitors)), maxP = Math.max(1, ...rows.map(r => r.views));
  const W = 900, H = 160, w = W / rows.length;
  const line = rows.map((r, i) => `${i * w + w / 2},${H - (r.views / maxP) * (H - 6)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H + 18}`} width="100%" role="img" aria-label="Visitors and page views by day" onMouseLeave={() => setHover(null)} style={{ display: 'block', overflow: 'visible' }}>
      {rows.map((r, i) => {
        const vh = (r.visitors / maxV) * (H - 6);
        return (
          <g key={r.day} onMouseEnter={() => setHover(i)}>
            <rect x={i * w + w * 0.15} y={H - vh} width={w * 0.7} height={vh} rx={Math.min(4, w / 4)} style={{ fill: 'var(--c-00897b)', opacity: hover == null || hover === i ? 0.9 : 0.55 }} />
            <rect x={i * w} y={0} width={w} height={H} fill="transparent" />
          </g>
        );
      })}
      <polyline points={line} fill="none" strokeWidth="2.5" strokeLinejoin="round" style={{ stroke: 'var(--c-1e88e5)' }} pointerEvents="none" />
      {[0, Math.floor(rows.length / 2), rows.length - 1].map(i => rows[i] && (
        <text key={i} x={i * w + w / 2} y={H + 14} fontSize="11" textAnchor="middle" style={{ fill: 'var(--t-90a4ae)' }}>{new Date(`${rows[i].day}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</text>
      ))}
      {hover != null && (() => {
        const r = rows[hover], x = Math.min(Math.max(hover * w + w / 2 - 80, 0), W - 160);
        return (
          <g pointerEvents="none">
            <rect x={x} y={4} width="160" height="52" rx="8" style={{ fill: 'var(--c-263238)', opacity: 0.94 }} />
            <text x={x + 10} y={22} fontSize="12" fontWeight="700" style={{ fill: 'var(--t-ffffff)' }}>{new Date(`${r.day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</text>
            <text x={x + 10} y={38} fontSize="11.5" style={{ fill: 'var(--t-ffffff)' }}>{num(r.visitors)} visitors · {num(r.visits)} visits</text>
            <text x={x + 10} y={51} fontSize="11.5" style={{ fill: 'var(--t-ffffff)' }}>{num(r.views)} page views</text>
          </g>
        );
      })()}
    </svg>
  );
}
function Hours({ hours }) {
  const max = Math.max(1, ...hours);
  return (
    <div style={S.hours}>
      {hours.map((n, h) => (
        <div key={h} style={S.hourCol} title={`${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}: ${num(n)} page views`}>
          <div style={{ ...S.hourBar, height: `${Math.max(n ? 4 : 0, (n / max) * 100)}%` }} />
          <div style={S.hourLabel}>{h % 6 === 0 ? `${h % 12 || 12}${h < 12 ? 'a' : 'p'}` : ''}</div>
        </div>
      ))}
    </div>
  );
}

const S = {
  bar: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 },
  refresh: { marginLeft: 'auto', padding: '7px 14px', borderRadius: 20, border: '1.5px solid var(--c-b2dfdb)', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, marginBottom: 16 },
  card: { background: 'var(--c-ffffff)', border: '1px solid var(--b-e8f5e9)', borderRadius: 14, padding: '14px 16px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.05)' },
  cardLabel: { fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 6 },
  cardValue: { fontSize: 28, fontWeight: 800, color: 'var(--t-00695c)', margin: '4px 0 2px' },
  cardSub: { fontSize: 12, color: 'var(--t-78909c)' },
  liveDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--c-43a047)', marginLeft: 'auto', boxShadow: '0 0 0 4px rgb(var(--c-43a047-rgb) / 0.18)' },
  panel: { background: 'var(--c-ffffff)', border: '1px solid var(--b-e8f5e9)', borderRadius: 14, padding: '14px 18px', marginBottom: 16, minWidth: 0 },   // minWidth: long names can't widen the grid
  sortBtn: { background: 'none', border: 'none', padding: 0, margin: 0, color: 'inherit', font: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' },
  h3: { margin: '0 0 12px', fontSize: 15, color: 'var(--t-00695c)', display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' },
  note: { fontSize: 12, fontWeight: 500, color: 'var(--t-90a4ae)', marginLeft: 6 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 0 },
  listRow: { display: 'flex', alignItems: 'center', gap: 10 },
  listBar: { position: 'relative', flex: 1, minWidth: 0, borderRadius: 6, overflow: 'hidden', background: 'var(--c-f5faf9)' },
  listFill: { position: 'absolute', left: 0, top: 0, bottom: 0, background: 'linear-gradient(90deg, var(--c-b2dfdb), var(--c-e0f2f1))' },
  listLabel: { position: 'relative', display: 'block', padding: '4px 8px', fontSize: 13, color: 'var(--t-37474f)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  listNum: { fontSize: 13, color: 'var(--t-37474f)', minWidth: 36, textAlign: 'right' },
  hours: { display: 'flex', alignItems: 'flex-end', gap: 3, height: 120 },
  hourCol: { flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' },
  hourBar: { width: '100%', borderRadius: '4px 4px 0 0', background: 'linear-gradient(180deg, var(--c-26a69a), var(--c-00897b))' },
  hourLabel: { fontSize: 10.5, color: 'var(--t-90a4ae)', height: 14, marginTop: 3 },
  tableWrap: { overflow: 'auto', borderRadius: 10, border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '10px 12px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '9px 12px', fontSize: 13, color: 'var(--t-333333)' },
  empty: { textAlign: 'center', padding: '30px 0', color: 'var(--t-78909c)', fontSize: 13 },
  muted: { color: 'var(--t-90a4ae)', fontSize: 13 },
  code: { fontSize: 12, color: 'var(--t-37474f)', background: 'var(--c-eceff1)', padding: '2px 6px', borderRadius: 4 },
  foot: { fontSize: 11.5, color: 'var(--t-90a4ae)', margin: '4px 0 0' },
  legend: { display: 'flex', gap: 16, fontSize: 12, color: 'var(--t-546e7a)', marginBottom: 8 },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 },
  fInput: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, color: 'var(--t-333333)', fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  clear: { padding: '7px 14px', borderRadius: 20, border: '1.5px solid var(--c-b2dfdb)', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' },
  count: { fontSize: 13, color: 'var(--t-546e7a)', marginBottom: 8 },
  tag: { display: 'inline-block', marginTop: 4, padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 },
  tagNew: { background: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)' },
  tagBack: { background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  key: { display: 'inline-block', width: 12, height: 12, borderRadius: 3, marginRight: 6, verticalAlign: '-2px' },
};
